import { atom, useAtom, useSetAtom } from "jotai";
import React, { createContext, useContext, useEffect, useMemo } from "react";
import { CSSProperties } from "react";

type CursorType = CSSProperties["cursor"];

interface BodyCursorTypeContextState {
  setCursorType: (cursor: CursorType) => void;
  cursorType: CursorType;
}

const BodyCursorTypeContext = createContext<
  BodyCursorTypeContextState | undefined
>(undefined);

export const cursorTypeAtom = atom<CursorType>("auto");

export const useBodyCursorType = (): BodyCursorTypeContextState => {
  const context = useContext(BodyCursorTypeContext);
  if (!context) {
    throw new Error(
      "useCursorType must be used within a CursorTypeContextProvider",
    );
  }
  return context;
};

export const useSetBodyCursorType = (): ((cursor: CursorType) => void) =>
  useSetAtom(cursorTypeAtom);

export const BodyCursorTypeContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }): React.ReactElement => {
  const [cursorType, setCursorType] = useAtom(cursorTypeAtom);
  useEffect(() => {
    document.body.style.cursor = cursorType || "auto";
  }, [cursorType]);
  const value: BodyCursorTypeContextState = useMemo(
    () => ({ setCursorType, cursorType }),
    [setCursorType, cursorType],
  );
  return (
    <BodyCursorTypeContext.Provider value={value}>
      {children}
    </BodyCursorTypeContext.Provider>
  );
};
